import { createAsyncThunk } from '@reduxjs/toolkit';
import { doc, getDoc, updateDoc, serverTimestamp, increment, arrayUnion, arrayRemove } from 'firebase/firestore';
import { db } from '../../../firebaseConfig.js';
import { getApp } from 'firebase/app';
import { auth } from '../../../firebaseConfig.js';
import { setUpvoteStatus, setLikeStatus } from '../solutionSlice.js';

/**
 * Upvote/downvote a solution
 */
export const upvoteSolution = createAsyncThunk(
  'solutions/upvote',
  async ({ solutionId, userId, hasUpvoted }, { getState, rejectWithValue, dispatch }) => {
    try {
      if (!solutionId || !userId) {
        throw new Error('Solution ID and User ID are required');
      }

      const solutionRef = doc(db, 'solutions', solutionId);
      const solutionDoc = await getDoc(solutionRef);

      if (!solutionDoc.exists()) {
        throw new Error('Solution not found');
      }

      // Optimistic update
      dispatch(setUpvoteStatus({ hasUpvoted, userId }));

      // Update in Firestore
      await updateDoc(solutionRef, {
        upvotes: hasUpvoted
          ? (solutionDoc.data().upvotes || 0) + 1
          : Math.max(0, (solutionDoc.data().upvotes || 1) - 1),
        voters: hasUpvoted
          ? arrayUnion(userId)
          : arrayRemove(userId),
        updatedAt: serverTimestamp()
      });

      return { hasUpvoted, userId };

    } catch (error) {
      console.error('Error upvoting solution:', error);
      // Revert optimistic update
      dispatch(setUpvoteStatus({ hasUpvoted: !hasUpvoted, userId }));
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Toggle like for a solution
 */
export const toggleLikeSolution = createAsyncThunk(
  'solutions/toggleLike',
  async ({ solutionId, userId, like }, { rejectWithValue, dispatch }) => {
    try {
      if (!solutionId || !userId) {
        throw new Error('Solution ID and User ID are required');
      }

      // Debug: verify project linkage and auth state
      const app = getApp();
      const projectId = app?.options?.projectId;
      console.debug('[toggleLikeSolution] start', {
        solutionId,
        userId,
        like,
        projectId,
        authUid: auth?.currentUser?.uid
      });

      const solutionRef = doc(db, 'solutions', solutionId);
      const solutionDoc = await getDoc(solutionRef);

      if (!solutionDoc.exists()) {
        throw new Error('Solution not found');
      }

      const data = solutionDoc.data() || {};
      const likers = Array.isArray(data.likers) ? data.likers : [];
      const hadLiked = likers.includes(userId);
      const postId = data.postId;

      // Optimistic update for current solution and list-level
      dispatch(setLikeStatus({ liked: like, userId, solutionId, postId }));

      // Persist to Firestore atomically
      console.debug('[toggleLikeSolution] updateDoc', {
        docPath: `solutions/${solutionId}`,
        like,
        projectId
      });
      // Only adjust count when membership actually changes
      const updates = { updatedAt: serverTimestamp() };
      if (like && !hadLiked) {
        updates.likes = increment(1);
        updates.likers = arrayUnion(userId);
      } else if (!like && hadLiked) {
        updates.likes = increment(-1);
        updates.likers = arrayRemove(userId);
      } else {
        updates.likers = like ? arrayUnion(userId) : arrayRemove(userId);
      }

      await updateDoc(solutionRef, updates);

      return { liked: like, userId };
    } catch (error) {
      // Provide richer diagnostics for permission errors
      try {
        const app = getApp();
        const projectId = app?.options?.projectId;
        console.error('[toggleLikeSolution] Firestore update failed', {
          code: error?.code,
          name: error?.name,
          message: error?.message,
          projectId,
          authUid: auth?.currentUser?.uid
        });
      } catch (e) {
        console.error('[toggleLikeSolution] Firestore update failed (no app)', error);
      }
      // Revert optimistic update
      dispatch(setLikeStatus({ liked: !like, userId, solutionId }));
      return rejectWithValue(error.message);
    }
  }
);
